/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, useFormContext } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

import { Button, StepLabel } from '@mui/material';
import { styled } from '@mui/material/styles';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import { StepIconProps } from '@mui/material/StepIcon';

import { DeviationWorkflowAction } from 'src/infra/types/deviation';
import { useSubmitDisputeWorkflow, useUpdateDispute } from 'src/use-cases/invoice/useDispute';
import { useGetInvoiceReport, useInvoiceSummaryFromParams } from 'src/use-cases/invoice/useInvoices';
import { beautifyEnum } from 'src/utils/utility';
import { DisputeFormToBody } from 'src/utils/invoiceMapper';
import { invoiceAndDisputeDetailsToDisputeFormValues } from 'src/utils/disputeMapper';
import { downloadZip } from 'src/utils/downloadFile';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';

import Modal, { ModalConfirmCustom } from 'src/presentations/components/molecules/modal';
import ButtonCustom from 'src/presentations/components/atom/button';
import { TransportMedium } from 'src/presentations/components/molecules/card/types';
import TransportMediumIcon from 'src/presentations/components/molecules/card/TransportMediumIcon';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';
import { useToastResult } from 'src/presentations/components/molecules/toast/useToastResult';
import acceptIcon from 'src/assets/images/icons/document-text-green.svg';
import rejectIcon from 'src/assets/images/icons/close-circle.svg';
import cancelIcon from 'src/assets/images/icons/back-square.svg';

import { FormContainer } from '../../forms/FormContainer';
import { useShipmentSelector } from '../ShipmentSelector';
import { validationSchema } from './formValidationSchema';
import { MainContent } from './MainContent';
import { IDisputeFormValues, DisputeStep } from './types';
import { DisputeFooter } from './DisputeFooter';
import { DisputeContext, IDisputeContext } from './DisputeContext';

const steps = [
  { id: DisputeStep.Step1_ContactServiceProvider, label: 'Contact Service Provider' },
  { id: DisputeStep.Step2_Resolution, label: 'Resolution' },
  { id: DisputeStep.Step3_Summary, label: 'Summary' },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const StyledModal = styled(Modal)({
  '& .MuiDialog-paper': {
    maxWidth: '1000px',
    height: 'calc(100% - 64px)',
    borderRadius: '16px',

    '& .MuiDialogContent-root': {
      padding: '20px 0',
    },
  },
});

const StyledStepper = styled(Stepper)({
  '& .MuiStepConnector-root': {
    padding: '0 8px',
  },
  '& .MuiStepConnector-line': {
    borderTopStyle: 'dashed',
    borderTopWidth: '2px',
  },
  '& .Mui-active .MuiStepConnector-line, & .Mui-completed .MuiStepConnector-line': {
    borderTopStyle: 'solid',
    borderColor: '#00992B',
  },
  '& .MuiStepLabel-label.Mui-active, & .MuiStepLabel-label.Mui-completed': {
    color: '#00992B',
    fontWeight: 'bold',
  },
  '& .MuiStepIcon-root': {
    color: '#ffffff',
    width: '32px',
    height: '32px',
    border: '1px solid #DFDFDF',
    borderRadius: '100%',

    '& .MuiStepIcon-text': {
      fill: '#00992B',
      fontWeight: 'bold',
    },

    '&.Mui-active': {
      color: '#00992B',
      borderColor: '#00992B',

      '& .MuiStepIcon-text': {
        fill: '#ffffff',
      },
    },
  },
});

interface IDisputeContextProps {
  onClose: () => void;
}

export const Dispute = ({ onClose }: IDisputeContextProps) => {
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment } = useShipmentSelector();
  const { invoiceDispute } = invoiceSummary;
  const navigate = useNavigate();
  const { showToastResult } = useToastResult();
  const queryClient = useQueryClient();

  const isDraft = invoiceDispute?.resolutionDescription && invoiceDispute?.resolutionDescription !== '';
  const [activeStep, setActiveStep] = useState<DisputeStep>(isDraft ? DisputeStep.Step2_Resolution : DisputeStep.Step1_ContactServiceProvider);
  const [isDisableSave, setIsDisableSave] = useState<boolean>(false);
  const [isShowActionConfirm, setIsShowActionConfirm] = useState<boolean>(false);
  const { checkVersion } = useInvoiceContext();
  const { mutateAsync: updateDispute } = useUpdateDispute();
  const { mutateAsync: submitDisputeWorkflow } = useSubmitDisputeWorkflow();
  const { refetch: refetchInvoiceReport } = useGetInvoiceReport(invoiceSummary?.id);

  const defaultValues = useMemo(() => invoiceAndDisputeDetailsToDisputeFormValues(invoiceSummary), [invoiceSummary]);

  const formContext = useForm({
    defaultValues: defaultValues as IDisputeFormValues,
    resolver: yupResolver(validationSchema),
  });

  const formValues = formContext.watch();
  const { agreementType } = formValues;
  const isSummaryAcceptOrReject = agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' || agreementType === 'REJECT_INVOICE';

  // We reset the fields with defaultValues to load the draft to form
  useEffect(() => {
    formContext.reset(defaultValues);
  }, [invoiceDispute]);

  // Check when the agreement type is change
  useEffect(() => {
    // If user change to different type with the current one
    if (formValues.agreementType !== defaultValues?.agreementType) {
      // Reset the form with null values except common fields
      formContext.reset({
        agreementType: formValues.agreementType,
        reason: invoiceDispute?.resolutionDescription ?? '',
        disputeTableItems: defaultValues.disputeTableItems,
      });
    }
  }, [formValues.agreementType]);

  /**
   * onErrorVersion
   */
  const onErrorVersion = () => {
    setIsDisableSave(true);
  };

  /**
   * Redirect to Audit dashboard
   */
  const goBack = useCallback(() => {
    navigate(`/dashboard?provider=${invoiceSummary?.serviceProvider}&modality=${invoiceSummary?.modality}`);
  }, [navigate, invoiceSummary?.modality, invoiceSummary?.serviceProvider]);

  /**
   * Prepare form values with correct totalAmount based on amountOption
   * This ensures the payload always has the correct totalAmount
   */
  const prepareFormValues = (values: IDisputeFormValues): IDisputeFormValues => {
    const { amountOption, otherAmount } = values;

    // Calculate totalAmount based on selected option
    if (amountOption === 'FULL_INVOICE' && invoiceSummary?.invoiceAmount?.afterTax !== undefined) {
      return {
        ...values,
        totalAmount: invoiceSummary.invoiceAmount.afterTax.toString(),
      };
    } else if (amountOption === 'DEVIATIONS' && invoiceSummary?.invoiceAmountDeviation?.afterTax !== undefined) {
      return {
        ...values,
        totalAmount: invoiceSummary.invoiceAmountDeviation.afterTax.toString(),
      };
    } else if (amountOption === 'OTHER' && otherAmount) {
      // For OTHER option, use the otherAmount field
      return {
        ...values,
        totalAmount: otherAmount,
      };
    }

    // Fallback
    return values;
  };

  /**
   * onSaveDraft
   */
  const onSaveDraft = async () => {
    checkVersion(async () => {
      const preparedValues = prepareFormValues(formValues as IDisputeFormValues);
      await updateDispute({ disputeId: invoiceDispute?.id, data: DisputeFormToBody(preparedValues, invoiceDispute?.id) });

      onClose();
    }, onErrorVersion);
  };

  /**
   * onSend
   */
  const onSend = async () => {
    setIsShowActionConfirm(true);
  };

  /**
   * onReject
   */
  const onReject = async () => {
    setIsShowActionConfirm(true);
  };

  /**
   * onSubmitAction
   */
  const onSubmitAction = async () => {
    if (agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE') {
      await onSubmitSend();
    } else if (agreementType === 'CANCEL_DISPUTE') {
      await onSubmitCancel();
    } else if (agreementType === 'REJECT_INVOICE') {
      await onSubmitReject();
    }

    // Close the confirmation modal
    setIsShowActionConfirm(false);

    // Handle redirection based on action type
    if (agreementType === 'CANCEL_DISPUTE') {
      // For cancel dispute, just close the modal and stay on current page
      onClose();
    } else {
      // For accept/reject, close modal and redirect to dashboard
      onClose();
      goBack();
    }
  };

  /**
   * onSubmitSend
   */
  const onSubmitSend = async () => {
    checkVersion(async () => {
      await submitDisputeWorkflow({ disputeId: invoiceDispute?.id, deviationWorkflowAction: invoiceSummary?.nextWorkflowAction });

      // Invalidate basic dispute-related caches
      await queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.dispute(invoiceDispute?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary?.id));

      showToastResult(invoiceSummary?.id, DeviationWorkflowAction.READY_FOR_PAYMENT, invoiceSummary?.invoiceNumber, shipment?.shipmentDetail?.shipmentNumber);
    }, onErrorVersion);
  };

  /**
   * onSubmitReject
   */
  const onSubmitReject = async () => {
    checkVersion(async () => {
      await submitDisputeWorkflow({
        disputeId: invoiceDispute?.id,
        deviationWorkflowAction: invoiceSummary?.nextWorkflowAction,
        reason: formValues?.reason || '',
      });

      // Invalidate basic dispute-related caches
      await queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.dispute(invoiceDispute?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary?.id));

      showToastResult(invoiceSummary?.id, DeviationWorkflowAction.REJECT_INVOICE, invoiceSummary?.invoiceNumber, shipment?.shipmentDetail?.shipmentNumber);
    }, onErrorVersion);
  };

  /**
   * onSubmitCancel
   */
  const onSubmitCancel = async () => {
    checkVersion(async () => {
      await submitDisputeWorkflow({
        disputeId: invoiceDispute?.id,
        deviationWorkflowAction: invoiceSummary?.nextWorkflowAction,
        reason: formValues?.reason || '',
      });

      // Invalidate basic dispute-related caches
      await queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.dispute(invoiceDispute?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary?.id));

      showToastResult(invoiceSummary?.id, DeviationWorkflowAction.CANCEL_DISPUTE, invoiceSummary?.invoiceNumber, shipment?.shipmentDetail?.shipmentNumber);
    }, onErrorVersion);
  };

  /**
   * onSuccessSubmit
   */
  const onSuccessSubmit = async () => {
    checkVersion(async () => {
      const preparedValues = prepareFormValues(formValues as IDisputeFormValues);
      await updateDispute({ disputeId: invoiceDispute?.id, data: DisputeFormToBody(preparedValues, invoiceDispute?.id) });

      // Invalidate basic dispute-related caches
      await queryClient.invalidateQueries(cacheKeysUtil.actionDetails(invoiceSummary?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.dispute(invoiceDispute?.id));
      await queryClient.invalidateQueries(cacheKeysUtil.invoiceSummary(invoiceSummary?.id));

      // Invalidate both approval and deviation questionnaire caches

      setActiveStep(DisputeStep.Step3_Summary);
    }, onErrorVersion);
  };

  /**
   * onDownloadInvoiceReport
   */

  const onDownloadInvoiceReport = async () => {
    const result = await refetchInvoiceReport();

    downloadZip(result, 'invoice-report');
  };

  const contextValue: IDisputeContext = useMemo(
    () => ({
      onReject,
      onSaveDraft,
      onSend,
      onSuccessSubmit,
      activeStep,
      setActiveStep,
      onDownloadInvoiceReport,
      onClose,
      isSummaryAcceptOrReject,
      setIsDisableSave,
      isDisableSave,
    }),
    [
      onReject,
      onSaveDraft,
      onSend,
      onSuccessSubmit,
      activeStep,
      setActiveStep,
      onDownloadInvoiceReport,
      onClose,
      isSummaryAcceptOrReject,
      setIsDisableSave,
      isDisableSave,
    ]
  );

  const isAgreementAccept = agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE';

  return (
    <DisputeContext.Provider value={contextValue}>
      <FormContainer formContext={formContext} FormProps={{ className: 'h-full flex flex-col' }}>
        <DisputeChildren />
        <ModalConfirmCustom
          open={isShowActionConfirm}
          title='Confirmation'
          onAccept={onSubmitAction}
          onCancel={() => {
            setIsShowActionConfirm(null);
          }}
          buttonAcceptLabel='Confirm'
          buttonChildren={
            <>
              <ButtonCustom
                variant='secondary'
                onClick={() => {
                  setIsShowActionConfirm(null);
                }}
              >
                Cancel
              </ButtonCustom>
              <ButtonCustom
                className={`${isAgreementAccept ? 'bg-[#00992B] hover:bg-[#085D3A]' : 'bg-[#E72547] hover:bg-[#b71c1c]'}`}
                variant='primary'
                onClick={onSubmitAction}
              >
                Confirm
              </ButtonCustom>
            </>
          }
        >
          <p className='text-14 xl:text-16'>
            {agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' && 'Are you sure you want to approve this invoice with credit note?'}
            {agreementType === 'REJECT_INVOICE' && 'Are you sure you want to reject the invoice?'}
            {agreementType === 'CANCEL_DISPUTE' && 'Are you sure you want to cancel the dispute?'}
          </p>
        </ModalConfirmCustom>
      </FormContainer>
    </DisputeContext.Provider>
  );
};

const CustomStepIcon = (props: StepIconProps) => {
  const { active, completed, icon } = props;

  return (
    <span
      className={clsx('flex items-center justify-center w-36 h-36 text-18 xl:text-20 font-bold rounded-full', {
        'bg-primary/25 text-primary': completed,
        'bg-primary text-white': active,
        'bg-white text-primary border border-neutral-20': !active && !completed,
      })}
    >
      {icon}
    </span>
  );
};

const DisputeChildren: React.FC<any> = () => {
  const { activeStep, onDownloadInvoiceReport } = useContext(DisputeContext);
  const { data: invoiceSummary } = useInvoiceSummaryFromParams();
  const { shipment } = useShipmentSelector();
  const { watch } = useFormContext<IDisputeFormValues>();
  const agreementType = watch('agreementType');

  return (
    <>
      <div className='px-16 xl:px-24 pb-24 border-b border-neutral-20'>
        <div className='py-10 xl:py-12 px-16 xl:px-24 bg-neutral-10 rounded-8 mb-20'>
          <StyledStepper activeStep={activeStep}>
            {steps.map(({ id, label }) => (
              <Step key={id}>
                <StepLabel color='inherit' StepIconComponent={CustomStepIcon}>
                  {label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>
        </div>

        <div className='flex items-end'>
          <div>
            <div className='flex items-center mb-20 xl:mb-24 mt-5'>
              <TransportMediumIcon medium={invoiceSummary?.modality as TransportMedium} iconColor='#9F9F9F' />
              <strong className='text-blue_grey-90 text-18 xl:text-24 font-bold mr-5 ml-16 xl:ml-20'>
                {beautifyEnum(invoiceSummary?.freightForwarder?.code)?.toUpperCase()}
              </strong>
              <span className='relative top-2 leading-1 text-16 xl:text-20 font-normal text-grey-15  mr-5 before:content-["•"] before:mx-1' />
              <span className='text-14 xl:text-16 font-normal text-neutral-60 leading-0'>{invoiceSummary?.modality}</span>
            </div>
            <div className='flex items-center gap-8 mb-2'>
              {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' && (
                <img src={acceptIcon} alt='Accept Icon' className='w-32 h-32' />
              )}
              {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'REJECT_INVOICE' && (
                <img src={rejectIcon} alt='Reject Icon' className='w-32 h-32' />
              )}
              {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'CANCEL_DISPUTE' && (
                <img src={cancelIcon} alt='Cancel Icon' className='w-32 h-32' />
              )}
              <h2 className='text-18 leading-24 font-bold'>
                {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'ACCEPT_INVOICE_WITH_CREDIT_NOTE' && 'Accept Invoice'}
                {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'REJECT_INVOICE' && 'Reject Invoice'}
                {activeStep >= DisputeStep.Step2_Resolution && agreementType === 'CANCEL_DISPUTE' && 'Cancel Dispute'}
                {(activeStep < DisputeStep.Step2_Resolution || !agreementType) && 'Dispute Process'}
              </h2>
            </div>
            <div className='flex items-center gap-8 xl:gap-12 text-14 leading-18 text-blue_grey-80'>
              <div>
                Invoice No. <strong className='text-primary'>{invoiceSummary?.invoiceNumber}</strong>
              </div>
              <div className='w-1 h-14 bg-neutral-20' />
              <div>
                <strong className='text-primary'>{shipment?.shipmentDetail?.shipmentNumber}</strong>
              </div>
            </div>
          </div>
          <div className='ml-auto pl-12'>
            <Button type='button' variant='outlined' color='primary' size='medium' sx={{ textTransform: 'capitalize' }} onClick={onDownloadInvoiceReport}>
              Download Invoice Report
            </Button>
          </div>
        </div>
      </div>
      <MainContent activeStep={activeStep} invoiceSummary={invoiceSummary} />
      <DisputeFooter />
    </>
  );
};
